/**
 * The export algorithm, ported near-literally from the design handoff
 * (`mockup/README.md`, "Export algorithm").
 *
 * Everything is drawn at the picture's **native resolution** — never at stage scale — so the
 * board rect arrives here already in image px (ADR-0011) and no conversion happens in this
 * file at all.
 *
 * Drawing targets a minimal {@link Ctx2D} rather than `CanvasRenderingContext2D` directly.
 * A real 2D context satisfies it structurally, and tests can pass a recording fake and assert
 * the exact coordinate of every line and label — which is the only way to know the exported
 * cells line up with what the editor showed.
 */

import { GRID_N, cellSize, columnLabel, rowLabel, type ImageRect } from './geometry';

/** Blackout fill for the player sheet. */
export const DEFAULT_BLACKOUT = '#0d0e16';

/** Gridline and label colour when the user has not picked one. */
export const DEFAULT_LINE = '#e7e5fe';

/** A paint style. Widened to match `CanvasRenderingContext2D` so a real context is
 *  structurally assignable to {@link Ctx2D}; we only ever assign strings. */
export type PaintStyle = string | CanvasGradient | CanvasPattern;

/** The subset of the 2D canvas API the export uses. */
export interface Ctx2D {
  fillStyle: PaintStyle;
  strokeStyle: PaintStyle;
  lineWidth: number;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  save(): void;
  restore(): void;
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  fillText(text: string, x: number, y: number): void;
}

/** Which of the two sheets to draw. */
export type SheetKind = 'quiz' | 'solution';

export interface DrawOptions {
  /** The picture, drawn at its native size. */
  readonly image: CanvasImageSource;
  /** The picture's native dimensions, and therefore the canvas dimensions. */
  readonly natural: { readonly w: number; readonly h: number };
  /** Board rect in image px. */
  readonly rect: ImageRect;
  /** `quiz` blacks the cells out; `solution` leaves the picture showing through. */
  readonly kind: SheetKind;
  /** Gridline and ruler colour. */
  readonly lineColor?: string;
  /** Blackout colour for the quiz sheet. */
  readonly blackoutColor?: string;
  /** Ruler type scale, 0.7–2.4. */
  readonly labelScale?: number;
  /** Divisions per side. */
  readonly n?: number;
}

/** Gridline width: hairline-thin on small pictures, proportional on large ones. */
export const lineWidthFor = (naturalW: number): number => Math.max(1.5, naturalW / 700);

/** Ruler font size in image px. */
export const rulerFontFor = (cell: number, labelScale: number): number =>
  Math.max(9, cell * 0.3) * labelScale;

/**
 * Where the two rulers sit, in image pixels.
 *
 * **This is the single source of truth for ruler layout**, used by the export *and* by the
 * on-screen overlay (converted to stage px). They were previously computed separately with
 * different rules, so the editor did not predict the exported sheet: the on-screen font was
 * a fixed `11 * labelScale` while the export scaled with the cell, and the export flipped a
 * ruler inside the board at large label sizes while the overlay never did. See ADR-0012.
 */
export interface RulerLayout {
  /** Font size in image px, rounded as the prototype does. */
  readonly rf: number;
  /** Baseline y for the column letters, in image px (alphabetic baseline). */
  readonly columnY: number;
  /** True when the column letters sit inside the board for want of room above it. */
  readonly columnsFlipped: boolean;
  /** x for the row numbers, in image px. */
  readonly rowX: number;
  /** True when the row numbers sit inside the board for want of room to its left. */
  readonly rowsFlipped: boolean;
}

export function rulerLayout(
  rect: ImageRect,
  n: number = GRID_N,
  labelScale: number = 1,
): RulerLayout {
  const rf = Math.round(rulerFontFor(cellSize(rect, n), labelScale));

  // Each ruler flips when its *drawn* position would push the label's own height off the
  // picture — not merely when the board touches the edge.
  const preferredY = rect.y - rf * 0.45;
  const columnsFlipped = preferredY - rf < 0;

  const preferredX = rect.x - rf * 0.5;
  const rowsFlipped = preferredX - rf < 0;

  return {
    rf,
    columnY: columnsFlipped ? rect.y + rf * 0.85 : preferredY,
    columnsFlipped,
    rowX: rowsFlipped ? rect.x + rf * 0.6 : preferredX,
    rowsFlipped,
  };
}

/**
 * Draw one sheet.
 *
 * Order matters and matches the handoff: picture, then blackout (quiz only), then gridlines,
 * then the double-width outer frame, then the rulers. The frame redraws the i=0 and i=n lines
 * at twice the width, which is intended.
 */
export function drawSheet(ctx: Ctx2D, opts: DrawOptions): void {
  const {
    image,
    natural,
    rect,
    kind,
    lineColor = DEFAULT_LINE,
    blackoutColor = DEFAULT_BLACKOUT,
    labelScale = 1,
    n = GRID_N,
  } = opts;

  const { x: gx, y: gy, size: gs } = rect;
  const cell = cellSize(rect, n);

  ctx.save();

  ctx.drawImage(image, 0, 0, natural.w, natural.h);

  if (kind === 'quiz') {
    ctx.fillStyle = blackoutColor;
    ctx.fillRect(gx, gy, gs, gs);
  }

  const lw = lineWidthFor(natural.w);
  ctx.strokeStyle = lineColor;

  // Gridlines — n + 1 lines per axis produce n cells.
  ctx.lineWidth = lw;
  for (let i = 0; i <= n; i += 1) {
    const offset = i * cell;

    ctx.beginPath();
    ctx.moveTo(gx + offset, gy);
    ctx.lineTo(gx + offset, gy + gs);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(gx, gy + offset);
    ctx.lineTo(gx + gs, gy + offset);
    ctx.stroke();
  }

  // Outer frame, double width.
  ctx.lineWidth = lw * 2;
  ctx.strokeRect(gx, gy, gs, gs);

  drawRulers(ctx, { rect, cell, n, lineColor, labelScale });

  ctx.restore();
}

interface RulerParams {
  rect: ImageRect;
  cell: number;
  n: number;
  lineColor: string;
  labelScale: number;
}

/**
 * Column letters above the board and row numbers to its left.
 *
 * Baselines and flip thresholds are taken from the working prototype rather than from the
 * handoff's prose, which is looser than the code it describes. Specifically: column letters
 * sit on an `alphabetic` baseline (not `middle`), and each ruler flips when its *drawn
 * position* would put the label's own height off the picture — `ry - rf < 0` for columns,
 * `rx - rf < 0` for rows — not merely when the board touches the edge.
 *
 * The font size is rounded, as the prototype does, so the two rulers agree with each other
 * and with the on-screen overlay at every label scale.
 */
function drawRulers(ctx: Ctx2D, p: RulerParams): void {
  const { rect, cell, n, lineColor, labelScale } = p;
  const { rf, columnY, rowX, rowsFlipped } = rulerLayout(rect, n, labelScale);

  ctx.fillStyle = lineColor;
  ctx.font = `500 ${rf}px Inter, system-ui, sans-serif`;

  // Columns: A, B, C … centred over each cell.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  for (let col = 0; col < n; col += 1) {
    ctx.fillText(columnLabel(col), rect.x + col * cell + cell / 2, columnY);
  }

  // Rows: 1, 2, 3 … centred beside each cell.
  ctx.textAlign = rowsFlipped ? 'left' : 'right';
  ctx.textBaseline = 'middle';
  for (let row = 0; row < n; row += 1) {
    ctx.fillText(rowLabel(row), rowX, rect.y + row * cell + cell / 2);
  }
}
